import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import api, { logoutClient } from './api';
import Spinner from './Spinner';
import HamburgerMenu from './HamburgerMenu';
import { isModuleLimitedUser, visibleHomeTiles } from './accessPolicy';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#212529',
        headerBg: isDark ? '#2a2a2a' : '#f8f9fa',
        border: isDark ? '#444' : '#dee2e6',
        link: isDark ? '#6cb2f7' : '#007bff',
        buttonText: isDark ? '#121212' : '#fff',
        menuItemBg: isDark ? '#333' : '#fff',
        menuItemColor: isDark ? '#e0e0e0' : '#333',
        containerBg: isDark ? '#121212' : '#fff',
        cardBg: isDark ? '#2a2a2a' : '#f8f9fa',
        success: isDark ? '#4caf50' : '#28a745',
        warning: isDark ? '#ff9800' : '#ffc107',
        danger: isDark ? '#f44336' : '#dc3545',
        info: isDark ? '#2196f3' : '#17a2b8',
    };

    return {
        container: { width: '90%', maxWidth: '1400px', margin: '20px auto', fontFamily: 'sans-serif', color: colors.text, backgroundColor: colors.containerBg },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '10px' },
        welcomeSection: { 
            backgroundColor: colors.cardBg, 
            padding: '20px', 
            borderRadius: '8px', 
            marginBottom: '30px',
            border: `1px solid ${colors.border}`
        },
        statsGrid: { 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '20px', 
            marginBottom: '30px' 
        },
        statCard: { 
            backgroundColor: colors.cardBg, 
            padding: '20px', 
            borderRadius: '8px', 
            border: `1px solid ${colors.border}`,
            textAlign: 'center'
        },
        statNumber: { 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '10px' 
        },
        statLabel: { 
            fontSize: '1.1rem', 
            color: colors.text,
            opacity: 0.8
        },
        quickActionsGrid: { 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '15px', 
            marginBottom: '30px' 
        },
        actionCard: { 
            backgroundColor: colors.cardBg, 
            padding: '20px', 
            borderRadius: '8px', 
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
            textDecoration: 'none',
            color: colors.text,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        },
        actionCardHover: {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 8px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`
        },
        actionIcon: { 
            fontSize: '2rem', 
            marginBottom: '10px' 
        },
        actionTitle: { 
            fontSize: '1.1rem', 
            fontWeight: 'bold',
            marginBottom: '5px'
        },
        actionDescription: { 
            fontSize: '0.9rem', 
            opacity: 0.8 
        },
        recentActivity: { 
            backgroundColor: colors.cardBg, 
            padding: '20px', 
            borderRadius: '8px', 
            border: `1px solid ${colors.border}` 
        },
        activityItem: { 
            padding: '10px 0', 
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        activityItemLast: { 
            borderBottom: 'none' 
        },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.menuItemColor,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.menuItemColor,
        },
        themeToggleButton: {
            padding: '8px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            backgroundColor: colors.background,
            color: colors.text,
            cursor: 'pointer',
            marginRight: '10px',
        },
        link: { color: colors.link, textDecoration: 'none' }
    };
};

function HomePage({ theme, toggleTheme }) {
    const styles = getStyles(theme);
    const [loading, setLoading] = useState(true);
    const [summaryData, setSummaryData] = useState({
        totalInspections: 0,
        completedInspections: 0,
        inProgressInspections: 0,
        notStartedInspections: 0,
        overdueInspections: 0,
        recentActivity: []
    });
    const [aircraftSummaryData, setAircraftSummaryData] = useState({
        totalAircraft: 0,
        upAircraft: 0,
        crossCountryAircraft: 0,
        unscheduledMaintenanceAircraft: 0,
        plannedMaintenanceAircraft: 0,
        downForSupplyAircraft: 0,
        awaitingPartsAircraft: 0,
        functionalCheckFlightAircraft: 0,
        engineeringAircraft: 0,
        detachmentAircraft: 0,
        otherStatusAircraft: 0
    });
    const [ltdSummaryData, setLTDSummaryData] = useState({
        totalLTD: 0,
        lowLTD: 0,
        mediumLTD: 0,
        highLTD: 0
    });

    const navigate = useNavigate();
    const { user, isStaff, groups } = useAuth();
    const auth = { isStaff, groups };
    const limitedModules = isModuleLimitedUser(auth);
    const homeTiles = visibleHomeTiles(auth);
    const showManningTile = homeTiles.some((tile) => tile.id === 'manning');
    const showUploadTile = homeTiles.some((tile) => tile.id === 'upload-data');

    const handleLogout = useCallback(() => {
        logoutClient();
        navigate('/login');
    }, [navigate]);

    // Create a stable string representation of groups for dependency comparison
    const groupsKey = useMemo(() => {
        return Array.isArray(groups) ? groups.sort().join(',') : '';
    }, [groups]);

    useEffect(() => {
        if (isModuleLimitedUser({ isStaff, groups })) {
            setLoading(false);
            return;
        }

        setLoading(true);
        
        // Fetch summary data
        Promise.all([
            api.get('/inspections/'),
            api.get('/inspections/?status=Completed'),
            api.get('/inspections/?status=In Progress'),
            api.get('/inspections/?status=Not Started'),
            api.get('/aircraft-status/'),
        ])
        .then(([allRes, completedRes, inProgressRes, notStartedRes, aircraftRes]) => {
            let allInspections = allRes.data.results || allRes.data || [];
            let completedInspections = completedRes.data.results || completedRes.data || [];
            let inProgressInspections = inProgressRes.data.results || inProgressRes.data || [];
            let notStartedInspections = notStartedRes.data.results || notStartedRes.data || [];
            const allAircraft = aircraftRes.data.results || aircraftRes.data || [];

            // Filter inspections by user's site groups (if not staff)
            if (!isStaff) {
                // Filter function to check if inspection belongs to user's site
                // Compare inspection.site with user's groups (case-insensitive)
                const filterBySite = (inspection) => {
                    if (!inspection.site) return false;
                    return groups.some(group => 
                        group.toUpperCase() === inspection.site.toUpperCase()
                    );
                };
                
                allInspections = allInspections.filter(filterBySite);
                completedInspections = completedInspections.filter(filterBySite);
                inProgressInspections = inProgressInspections.filter(filterBySite);
                notStartedInspections = notStartedInspections.filter(filterBySite);
            }

            // Calculate overdue inspections (ECD is in the past and status is not completed)
            const today = new Date();
            const overdueInspections = allInspections.filter(insp => 
                insp.ecd && 
                new Date(insp.ecd) < today && 
                insp.status !== 'Completed'
            );

            // Get recent activity (last 5 updated inspections)
            const recentActivity = allInspections
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                .slice(0, 5);

            //Calculate Long Term Down (LTD) aircraft based on Last Fly Date
            const calculateDaysSinceLastFly = (lastFlyDate) => {
                if (!lastFlyDate) return null;
                
                // Parse date manually to avoid timezone issues
                let flyDate;
                if (typeof lastFlyDate === 'string') {
                    // Handle ISO date strings (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
                    const dateParts = lastFlyDate.split('T')[0].split('-');
                    if (dateParts.length === 3) {
                        const year = parseInt(dateParts[0], 10);
                        const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
                        const day = parseInt(dateParts[2], 10);
                        flyDate = new Date(year, month, day); // Create date in local timezone
                    } else {
                        flyDate = new Date(lastFlyDate);
                    }
                } else if (lastFlyDate instanceof Date) {
                    flyDate = lastFlyDate;
                } else {
                    flyDate = new Date(lastFlyDate);
                }
                
                const today = new Date();
                // Reset both dates to midnight for accurate day calculation
                const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                const flyDateMidnight = new Date(flyDate.getFullYear(), flyDate.getMonth(), flyDate.getDate());
                const diffTime = todayMidnight - flyDateMidnight;
                return Math.floor(diffTime / (1000 * 60 * 60 * 24));
            };
            
            // Filter aircraft that have last fly dates and calculate LTD categories
            const aircraftWithLastFlyDate = allAircraft.filter(ac => ac.last_fly_date);
            
            const ltdAircraft = aircraftWithLastFlyDate.filter(ac => {
                const daysSinceLastFly = calculateDaysSinceLastFly(ac.last_fly_date);
                return daysSinceLastFly !== null && daysSinceLastFly >= 30;
            });
            
            const ltd30to60Days = ltdAircraft.filter(ac => {
                const daysSinceLastFly = calculateDaysSinceLastFly(ac.last_fly_date);
                return daysSinceLastFly >= 30 && daysSinceLastFly <= 60;
            });
            
            const ltd60to90Days = ltdAircraft.filter(ac => {
                const daysSinceLastFly = calculateDaysSinceLastFly(ac.last_fly_date);
                return daysSinceLastFly > 60 && daysSinceLastFly <= 90;
            });
            
            const ltdOver90Days = ltdAircraft.filter(ac => {
                const daysSinceLastFly = calculateDaysSinceLastFly(ac.last_fly_date);
                return daysSinceLastFly > 90;
            });
            
            
            // Calculate aircraft status summary
            const upAircraft = allAircraft.filter(ac => ac.status === 'UP').length;
            const crossCountryAircraft = allAircraft.filter(ac => ac.status === 'XC').length;
            const unscheduledMaintenanceAircraft = allAircraft.filter(ac => ac.status === 'DN').length;
            const plannedMaintenanceAircraft = allAircraft.filter(ac => ac.status === 'PM').length;
            const downForSupplyAircraft = allAircraft.filter(ac => ac.status === 'DS').length;
            const awaitingPartsAircraft = allAircraft.filter(ac => ac.status === 'AWP').length;
            const functionalCheckFlightAircraft = allAircraft.filter(ac => ac.status === 'FCF').length;
            const engineeringAircraft = allAircraft.filter(ac => ac.status === 'REI').length;
            const detachmentAircraft = allAircraft.filter(ac => ac.status === 'DET').length;
            const otherStatusAircraft = allAircraft.filter(ac => ac.status === 'OTH').length;
     
            setLTDSummaryData({
                totalLTD: ltdAircraft.length,
                lowLTD: ltd30to60Days.length,
                mediumLTD: ltd60to90Days.length,
                highLTD: ltdOver90Days.length
            });
            
            setSummaryData({
                totalInspections: allInspections.length,
                completedInspections: completedInspections.length,
                inProgressInspections: inProgressInspections.length,
                notStartedInspections: notStartedInspections.length,
                overdueInspections: overdueInspections.length,
                recentActivity: recentActivity
            });

            setAircraftSummaryData({
                totalAircraft: allAircraft.length,
                upAircraft: upAircraft,
                crossCountryAircraft: crossCountryAircraft,
                unscheduledMaintenanceAircraft: unscheduledMaintenanceAircraft,
                plannedMaintenanceAircraft: plannedMaintenanceAircraft,
                downForSupplyAircraft: downForSupplyAircraft,
                awaitingPartsAircraft: awaitingPartsAircraft,
                functionalCheckFlightAircraft: functionalCheckFlightAircraft,
                engineeringAircraft: engineeringAircraft,
                detachmentAircraft: detachmentAircraft,
                otherStatusAircraft: otherStatusAircraft
            });
        })
        .catch(err => {
            console.error("Error fetching summary data:", err);
            if (err.response && err.response.status === 401) handleLogout();
        })
        .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStaff, groupsKey]); // Use groupsKey instead of groups to prevent infinite loops. handleLogout is stable.


    const getStatColor = (theme, type) => {
        const colors = {
            success: theme === 'dark' ? '#4caf50' : '#28a745',
            warning: theme === 'dark' ? '#ff9800' : '#ffc107',
            danger: theme === 'dark' ? '#f44336' : '#dc3545',
            info: theme === 'dark' ? '#2196f3' : '#17a2b8',
        };
        return colors[type] || colors.info;
    };

    if (loading) {
        return <Spinner />;
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <HamburgerMenu theme={theme}/>
                <h1 style={{ flexGrow: 1, textAlign: 'center' }}>Home</h1>
                <button onClick={toggleTheme} style={styles.themeToggleButton}>
                    {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
                </button>
                <div style={{ width: '45px' }}></div>
            </div>

            <div style={styles.welcomeSection}>
                <h2>Welcome back, {user?.username || 'User'}!</h2>
                <p>
                    {limitedModules
                        ? `Use the ${showManningTile && showUploadTile ? 'tiles' : 'tile'} below to open the section assigned to your account.`
                        : "Here's a summary of your inspection activities and system status."}
                </p>
            </div>

            <div style={styles.quickActionsGrid}>
                {homeTiles.map((tile) => (
                    <Link key={tile.id} to={tile.path} style={styles.actionCard}>
                        <div style={styles.actionIcon}>{tile.icon}</div>
                        <div style={styles.actionTitle}>{tile.title}</div>
                        <div style={styles.actionDescription}>{tile.description}</div>
                    </Link>
                ))}
            </div>

            {!limitedModules && (
            <>
            <div style={{...styles.statsGrid, marginTop: '20px'}}>
                <h3 style={{gridColumn: '1 / -1', marginBottom: '10px', color: styles.text}}>Aircraft Status Summary: Total Aircraft {aircraftSummaryData.totalAircraft}</h3> 
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'success')}}>
                        {aircraftSummaryData.upAircraft}
                    </div>
                    <div style={styles.statLabel}>Up Aircraft</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'info')}}>
                        {aircraftSummaryData.crossCountryAircraft}
                    </div>
                    <div style={styles.statLabel}>Cross Country</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'danger')}}>
                        {aircraftSummaryData.unscheduledMaintenanceAircraft}
                    </div>
                    <div style={styles.statLabel}>Unscheduled Maintenance</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'warning')}}>
                        {aircraftSummaryData.plannedMaintenanceAircraft}
                    </div>
                    <div style={styles.statLabel}>Planned Maintenance</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'info')}}>
                        {aircraftSummaryData.functionalCheckFlightAircraft}
                    </div>
                    <div style={styles.statLabel}>Functional Check Flight</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'danger')}}>
                        {aircraftSummaryData.downForSupplyAircraft}
                    </div>
                    <div style={styles.statLabel}>Down for Supply</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'warning')}}>
                        {aircraftSummaryData.awaitingPartsAircraft}
                    </div>
                    <div style={styles.statLabel}>Awaiting Parts</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'info')}}>
                        {aircraftSummaryData.engineeringAircraft}
                    </div>
                    <div style={styles.statLabel}>Engineering</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'info')}}>
                        {aircraftSummaryData.detachmentAircraft}
                    </div>
                    <div style={styles.statLabel}>Detachment Aircraft</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'info')}}>
                        {aircraftSummaryData.otherStatusAircraft}
                    </div>
                    <div style={styles.statLabel}>Other Status</div>
                </div>
            </div>

            <div style={styles.statsGrid}>
                <h3 style={{gridColumn: '1 / -1', marginBottom: '10px', color: styles.text}}>Long Term Down Aircraft</h3>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'info')}}>
                        {ltdSummaryData.totalLTD}
                    </div>
                    <div style={styles.statLabel}>Total LTD</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'info')}}>
                        {ltdSummaryData.lowLTD}
                    </div>
                    <div style={styles.statLabel}>30 - 60 Day</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'warning')}}>
                        {ltdSummaryData.mediumLTD}
                    </div>
                    <div style={styles.statLabel}>60 - 90 Day</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'danger')}}>
                        {ltdSummaryData.highLTD}
                    </div>
                    <div style={styles.statLabel}>90 + Day</div>
                </div>
            </div>

            <div style={styles.statsGrid}>
                <h3 style={{gridColumn: '1 / -1', marginBottom: '10px', color: styles.text}}>Inspection Summary</h3>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'info')}}>
                        {summaryData.totalInspections}
                    </div>
                    <div style={styles.statLabel}>Total Inspections</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'success')}}>
                        {summaryData.completedInspections}
                    </div>
                    <div style={styles.statLabel}>Completed</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'warning')}}>
                        {summaryData.inProgressInspections}
                    </div>
                    <div style={styles.statLabel}>In Progress</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'info')}}>
                        {summaryData.notStartedInspections}
                    </div>
                    <div style={styles.statLabel}>Not Started</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statNumber, color: getStatColor(theme, 'danger')}}>
                        {summaryData.overdueInspections}
                    </div>
                    <div style={styles.statLabel}>Overdue</div>
                </div>
            </div>

            <div style={styles.recentActivity}>
                <h3>Recent Activity</h3>
                {summaryData.recentActivity.length > 0 ? (
                    summaryData.recentActivity.map((activity, index) => (
                        <div 
                            key={activity.id} 
                            style={{
                                ...styles.activityItem,
                                ...(index === summaryData.recentActivity.length - 1 ? styles.activityItemLast : {})
                            }}
                        >
                            <div>
                                <strong>{activity.buno}</strong> - {activity.checklist_type}
                                <br />
                                <small>Status: {activity.status} | Updated: {new Date(activity.updated_at).toLocaleString()}</small>
                            </div>
                            <div>
                                {activity.status === 'Completed' ? (
                                    <Link to={`/inspection/${activity.id}/report`} style={styles.link}>View Report</Link>
                                ) : activity.status === 'In Progress' ? (
                                    <Link to={`/inspection/${activity.id}`} style={styles.link}>Continue</Link>
                                ) : (
                                    <Link to={`/inspection/${activity.id}`} style={styles.link}>Start</Link>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <p>No recent activity to display.</p>
                )}
            </div>
            </>
            )}
        </div>
    );
}

export default HomePage;
